/**
 * AutoCompilerSection — Auto-Compiler Proxy v2 marketing surface
 *
 * Showcases the three-layer Auto-Compiler that wraps every LLM call going
 * through `/api/llm-proxy/v1/chat/completions`:
 *   1. Cache       (100% savings on duplicate / near-duplicate prompts)
 *   2. Compact     (15-40% input-token shrink via deterministic heuristics)
 *   3. Cascade     (80% savings — try cheap model first, escalate on low confidence)
 *
 * Includes a live "try it" widget that runs the compactor client-side so users
 * can see token reduction on their own prompt before they install anything.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Database, Scissors, GitBranch, Sparkles, Copy, Check, ArrowRight,
} from 'lucide-react';

// Client-side mirror of the backend heuristic compactor. Used ONLY for the
// preview widget — actual compaction always runs server-side in proxy_compactor.py.
function clientCompact(text) {
  if (!text) return text;
  let t = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  t = t.replace(/<!--[\s\S]*?-->/g, '');
  t = t.replace(/\n{3,}/g, '\n\n');
  t = t.replace(/[ \t]{2,}/g, ' ');
  t = t.replace(/[ \t]+(\n|$)/g, '$1');
  // sentence dedup
  if (t.length >= 25) {
    const parts = t.split(/(?<=[.!?])\s+/);
    const out = [];
    let prev = '';
    for (const p of parts) {
      const n = p.trim().toLowerCase();
      if (n && n === prev) continue;
      out.push(p);
      prev = n;
    }
    t = out.join(' ').trim();
  }
  return t.trim();
}

// Rough token estimate (matches tiktoken/cl100k within ~10% for English)
function estimateTokens(text) {
  if (!text) return 0;
  return Math.max(1, Math.round(text.length / 4));
}

const SAMPLE_PROMPT = `You are a helpful customer support assistant.
You are a helpful customer support assistant.



The user's name is John.    They have an enterprise account.
The user's name is John. They have an enterprise account.

<!-- internal: prefer concise replies -->

Question:   What is the refund policy?  `;

export default function AutoCompilerSection() {
  const [prompt, setPrompt] = useState(SAMPLE_PROMPT);
  const [copied, setCopied] = useState(false);

  const compacted = useMemo(() => clientCompact(prompt), [prompt]);
  const origTokens = useMemo(() => estimateTokens(prompt), [prompt]);
  const newTokens = useMemo(() => estimateTokens(compacted), [compacted]);
  const savedTokens = Math.max(0, origTokens - newTokens);
  const savedPct = origTokens > 0 ? Math.round((savedTokens / origTokens) * 100) : 0;

  const snippet = `from openai import OpenAI

# ONE line change — point base_url at CARTIE.
# Get a free key at cartieai.com/llm-proxy
client = OpenAI(
    api_key="<YOUR-OPENAI-KEY>",
    base_url="https://cartieai.com/api/llm-proxy/v1",
    default_headers={"X-Cartie-Key": "<YOUR-CARTIE-PROXY-KEY>"},
)

# Every call now: compacted → cached → cascaded.
# Returns standard OpenAI response. Saves 40-70%.
resp = client.chat.completions.create(
    model="gpt-5.2",
    messages=[{"role": "user", "content": "Hello"}],
)`;

  const copy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(snippet);
        ok = true;
      }
    } catch (_) { /* fall through */ }
    if (!ok) {
      // Legacy fallback for headless / non-secure contexts
      try {
        const ta = document.createElement('textarea');
        ta.value = snippet;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) { /* no-op */ }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      className="px-4 py-14 border-b border-white/5 bg-slate-950 bg-gradient-to-b from-slate-950 to-slate-950/60"
      data-testid="auto-compiler-section"
    >
      <div className="max-w-6xl mx-auto">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono mb-4"
          data-testid="auto-compiler-badge"
        >
          <Sparkles className="w-3.5 h-3.5" /> v2 · Auto-Compiler · LIVE
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="auto-compiler-heading">
          One line. Three savings layers. <span className="text-emerald-400">Zero code changes.</span>
        </h2>
        <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-2xl leading-relaxed">
          Point your existing OpenAI / Anthropic SDK at the CARTIE proxy. Every prompt is automatically
          compacted, cached, and cascade-routed. You get standard OpenAI responses back —
          plus 40-70% lower bills and full observability.
        </p>

        {/* Three-layer cards */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="auto-compiler-layers">
          <LayerCard
            icon={Scissors}
            accent="emerald"
            title="Compact"
            metric="15-40% fewer input tokens"
            desc="Whitespace, dedup, comments stripped before forwarding. Deterministic heuristics. <5ms."
            testid="layer-compact"
          />
          <LayerCard
            icon={Database}
            accent="sky"
            title="Cache"
            metric="100% savings on dupes"
            desc="Normalized-hash lookup (timestamps + UUIDs + emails masked). 24h TTL. Sub-ms."
            testid="layer-cache"
          />
          <LayerCard
            icon={GitBranch}
            accent="amber"
            title="Cascade"
            metric="80% savings on simple tasks"
            desc="Try cheap model first. Score confidence. Escalate to frontier only if needed."
            testid="layer-cascade"
          />
        </div>

        {/* Live compactor demo + install snippet */}
        <div className="mt-8 grid lg:grid-cols-2 gap-5">
          {/* Live demo */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="auto-compiler-tryit-card">
            <div className="text-xs font-mono uppercase tracking-wider text-emerald-300 mb-2">
              Try it · live in your browser
            </div>
            <label className="text-xs text-slate-400 block mb-1">Paste any prompt — we&apos;ll compact it client-side first.</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={7}
              className="w-full bg-slate-900 border border-white/10 rounded-md p-3 text-xs text-slate-200 font-mono leading-relaxed focus:border-emerald-500/40 focus:outline-none"
              data-testid="auto-compiler-prompt-input"
            />
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md bg-slate-900 border border-white/10 px-2 py-2" data-testid="metric-original">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Original</div>
                <div className="text-lg font-bold text-white tabular-nums">{origTokens}</div>
                <div className="text-[10px] text-slate-500">tokens</div>
              </div>
              <div className="rounded-md bg-slate-900 border border-white/10 px-2 py-2" data-testid="metric-compacted">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Compacted</div>
                <div className="text-lg font-bold text-emerald-400 tabular-nums">{newTokens}</div>
                <div className="text-[10px] text-slate-500">tokens</div>
              </div>
              <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-2 py-2" data-testid="metric-saved">
                <div className="text-[10px] text-emerald-300 uppercase tracking-wider">Saved</div>
                <div className="text-lg font-bold text-emerald-300 tabular-nums">{savedPct}%</div>
                <div className="text-[10px] text-emerald-400">{savedTokens} tokens</div>
              </div>
            </div>
            <details className="mt-3 group">
              <summary className="cursor-pointer text-xs text-slate-400 hover:text-emerald-300 select-none">
                Show compacted output ↓
              </summary>
              <pre className="mt-2 text-[11px] text-slate-300 font-mono bg-slate-950 border border-white/10 rounded-md p-3 whitespace-pre-wrap" data-testid="auto-compiler-compacted-output">{compacted || '(empty)'}</pre>
            </details>
          </div>

          {/* Install snippet */}
          <div className="rounded-2xl border border-emerald-500/20 bg-slate-900 bg-gradient-to-br from-emerald-500/5 via-slate-900 to-slate-900 p-5" data-testid="auto-compiler-install-card">
            <div className="text-xs font-mono uppercase tracking-wider text-emerald-300 mb-2">
              Drop-in install · Python
            </div>
            <div className="text-sm text-slate-300 mb-3">
              Same OpenAI SDK. New <code className="text-emerald-300 bg-slate-950 px-1.5 py-0.5 rounded">base_url</code>.
              Standard responses. 40-70% lower bill.
            </div>
            <div className="relative">
              <pre className="text-[11px] text-slate-200 font-mono bg-slate-950 border border-white/10 rounded-md p-3 overflow-x-auto leading-relaxed" data-testid="auto-compiler-snippet">{snippet}</pre>
              <button
                type="button"
                onClick={copy}
                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-slate-300 transition"
                data-testid="auto-compiler-copy-btn"
              >
                {copied ? <><Check className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/connect"
                data-testid="auto-compiler-cta-key"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition"
              >
                Try the 60-sec Connect <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/llm-proxy"
                data-testid="auto-compiler-cta-docs"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:border-emerald-500/40 text-white font-semibold text-sm transition"
              >
                Read the docs
              </Link>
            </div>
          </div>
        </div>

        {/* Response headers footnote */}
        <div className="mt-6 rounded-xl border border-white/10 bg-slate-900/60 p-4 text-xs text-slate-400 leading-relaxed" data-testid="auto-compiler-headers-info">
          <span className="text-emerald-300 font-mono">Every proxied call returns these headers</span> so your
          observability stack can track savings in real time:
          {' '}
          <code className="text-slate-200 bg-slate-950 px-1.5 py-0.5 rounded">X-Cartie-Saved-Usd</code>{' · '}
          <code className="text-slate-200 bg-slate-950 px-1.5 py-0.5 rounded">X-Cartie-Cache-Hit</code>{' · '}
          <code className="text-slate-200 bg-slate-950 px-1.5 py-0.5 rounded">X-Cartie-Tokens-Saved</code>{' · '}
          <code className="text-slate-200 bg-slate-950 px-1.5 py-0.5 rounded">X-Cartie-Cascade-Used</code>
        </div>
      </div>
    </section>
  );
}

function LayerCard({ icon: Icon, accent, title, metric, desc, testid }) {
  const ring = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    sky: 'border-sky-500/30 bg-sky-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
  }[accent];
  const text = {
    emerald: 'text-emerald-300',
    sky: 'text-sky-300',
    amber: 'text-amber-300',
  }[accent];
  return (
    <div className={`rounded-2xl border ${ring} p-5`} data-testid={testid}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${text} bg-slate-900 border border-white/10`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-3 text-lg font-bold text-white">{title}</div>
      <div className={`mt-1 text-sm font-mono ${text}`}>{metric}</div>
      <div className="mt-3 text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}
